import { AllCommunityModule, colorSchemeDark, ModuleRegistry, themeBalham, ColDef, GetRowIdParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { FinxaiMktData } from '../models/finxaiMktData';
import { RootState } from '../store/store';
import ContextMenu from './context-menu';
import OrderForm from './order-form';

ModuleRegistry.registerModules([AllCommunityModule]);

// Fallback data when marketData is empty - moved outside component
const fallbackData: FinxaiMktData[] = [
    //{ security_id: 'AAPL US', bid_px: 150.25, bid_sz: 500, offer_px: 150.30, offer_sz: 600, last_traded_px: 150.27, last_traded_sz: 100 },
    //{ security_id: 'MSFT US', bid_px: 300.15, bid_sz: 200, offer_px: 300.20, offer_sz: 250, last_traded_px: 300.18, last_traded_sz: 50 },
    //{ security_id: 'GOOGL US', bid_px: 2500.00, bid_sz: 20, offer_px: 2500.50, offer_sz: 18, last_traded_px: 2500.25, last_traded_sz: 10 },
];

// Column Definitions - moved outside component to prevent recreation
const colDefs: ColDef<FinxaiMktData>[] = [
    { field: 'security_id' as const, headerName: 'Instrument', width: 150 },
    { field: 'bid_px' as const, headerName: 'Bid', cellDataType: 'number', cellStyle: { textAlign: 'right' }, width: 75 },
    /*
    { 
        headerName: 'Mid', 
        cellDataType: 'number', 
        cellStyle: { textAlign: 'right' },
        valueGetter: (params: any) => {
            const bid = params.data?.bid_px;
            const offer = params.data?.offer_px;
            if (bid != null && offer != null) {
                return parseFloat(((bid + offer) / 2).toFixed(3));
            }
            return null;
        },
        width: 75,
    },
    */
    { field: 'offer_px' as const, headerName: 'Offer', cellDataType: 'number', cellStyle: { textAlign: 'right' }, width: 75 },
];


export type MarketDataFilterType = 'All' | 'Active' | 'Watchlist' | 'Custom';

interface MktDataGridProps {
    filterType?: MarketDataFilterType;
    customTickers?: string[];
    customDisplayNames?: Record<string, string>;
}

const MktDataGrid = React.memo(({ filterType = 'All', customTickers, customDisplayNames }: MktDataGridProps) => {
    // Context menu state
    const [contextMenu, setContextMenu] = useState<{ x: number; y: number; data: FinxaiMktData } | null>(null);
    
    // Order form state
    const [orderForm, setOrderForm] = useState<{ data: FinxaiMktData; side: 'BUY' | 'SELL' } | null>(null);

    // Get market data from Redux store - use shallowEqual to prevent unnecessary re-renders
    const marketData = useSelector(
        (state: RootState) => state.data.marketData,
        shallowEqual
    );
    
    // Get trade orders from Redux store to filter by securities with active orders
    const tradeOrders = useSelector(
        (state: RootState) => state.data.tradeOrders,
        shallowEqual
    );

    // Memoize row data to prevent unnecessary recalculations
    const effectiveRowData = useMemo(() => {
        const data = (marketData && marketData.length > 0) ? marketData : fallbackData;
        
        // Apply filtering based on filterType
        switch (filterType) {
            case 'Active':
                // Show instruments with recent trading activity (last_traded_sz > 0)
                return data.filter(item => item.last_traded_sz > 0);
            case 'Watchlist':
                // For now, return empty array as watchlist functionality can be added later
                // You can enhance this to filter based on a watchlist in Redux state
                return [];
                case 'Custom':
    if (customTickers && customTickers.length > 0) {
        const dataMap = new Map(data.map(item => [item.security_id, item]));
        return customTickers.map(ticker => {
            const mktData = dataMap.get(ticker);
            if (mktData) {
                return mktData;
            } else {
                return {
                    security_id: ticker,
                    bid_px: 0,
                    bid_sz: 0,
                    offer_px: 0,
                    offer_sz: 0,
                    last_traded_px: 0,
                    last_traded_sz: 0,
                } as FinxaiMktData;
            }
        });
    }
    return [];
            case 'All':
            default:
                // Show only securities that exist in trade orders (empty when no trades)
                const tradeOrderSecurityIds = new Set((tradeOrders || []).map(order => order.security_id));
                return data.filter(item => tradeOrderSecurityIds.has(item.security_id));
        }
    }, [marketData, filterType, customTickers, tradeOrders]);

    // Dynamic column definitions based on whether we have custom display names
    const effectiveColDefs = useMemo(() => {
        if (customDisplayNames) {
            return [
                { 
                    field: 'security_id' as const, 
                    headerName: 'Instrument',
                    valueFormatter: (params: any) => {
                        return customDisplayNames[params.value] || params.value;
                    },
                    width: 250,
                },
                { field: 'bid_px' as const, headerName: 'Bid Price', cellDataType: 'number', cellStyle: { textAlign: 'right' } },
                { 
                    headerName: 'Mid', 
                    cellDataType: 'number', 
                    cellStyle: { textAlign: 'right' },
                    valueGetter: (params: any) => {
                        const bid = params.data?.bid_px;
                        const offer = params.data?.offer_px;
                        if (bid != null && offer != null) {
                            return parseFloat(((bid + offer) / 2).toFixed(3));
                        }
                        return null;
                    }
                },
                { field: 'offer_px' as const, headerName: 'Offer Price', cellDataType: 'number', cellStyle: { textAlign: 'right' } },
            ];
        }
        return colDefs;
    }, [customDisplayNames]);

    // Provide unique row ID for AG Grid to track rows efficiently
    const getRowId = useMemo(() => (params: GetRowIdParams<FinxaiMktData>) => params.data.security_id, []);

    // Handle opening order form
    const handleOpenOrderForm = useCallback((data: FinxaiMktData, side: 'BUY' | 'SELL') => {
        setOrderForm({ data, side });
    }, []);

    // Handle closing order form
    const closeOrderForm = useCallback(() => {
        setOrderForm(null);
    }, []);

    // Handle row context menu (right-click)
    const onCellContextMenu = useCallback((event: any) => {
        event.event.preventDefault();
        if (event.data) {
            setContextMenu({
                x: event.event.clientX,
                y: event.event.clientY,
                data: event.data,
            });
        }
    }, []);

    // Close context menu
    const closeContextMenu = useCallback(() => {
        setContextMenu(null);
    }, []);

    // Close context menu and order form on escape key
    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                if (orderForm) {
                    closeOrderForm();
                } else if (contextMenu) {
                    closeContextMenu();
                }
            }
        };
        window.addEventListener('keydown', handleEscape);
        return () => window.removeEventListener('keydown', handleEscape);
    }, [closeContextMenu, closeOrderForm, contextMenu, orderForm]);

    return (
        <div 
            style={{ height: '100%', width: '100%' }}
            onContextMenu={(e) => e.preventDefault()}
        >
            <AgGridReact
                autoSizeStrategy={{ type: 'fitProvidedWidth', width: 400 }}
                theme={themeBalham.withPart(colorSchemeDark)}
                rowData={effectiveRowData}
                columnDefs={effectiveColDefs}
                getRowId={getRowId}
                onCellContextMenu={onCellContextMenu}
                animateRows={true}
            />
            {contextMenu && (
                <ContextMenu
                    key={contextMenu.data.security_id}
                    x={contextMenu.x}
                    y={contextMenu.y}
                    data={contextMenu.data}
                    onOpenOrderForm={handleOpenOrderForm}
                    onClose={closeContextMenu}
                />
            )}
            {orderForm && (
                <OrderForm
                    data={orderForm.data}
                    side={orderForm.side}
                    onClose={closeOrderForm}
                />
            )}
        </div>
    );
});

MktDataGrid.displayName = 'MktDataGrid';

export default MktDataGrid;


